import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, LogOut, Inbox, CheckCircle2, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Logo } from "@/components/Logo";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rm_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [passcode, setPasscode] = useState("");
  const [enquiries, setEnquiries] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const toggleReplied = async (q) => {
    try {
      const { data } = await axios.patch(`${API}/enquiries/${q.id}`, { replied: !q.replied }, auth());
      setEnquiries((list) => list.map((e) => (e.id === q.id ? data : e)));
    } catch {
      toast.error("Could not update that enquiry.");
    }
  };

  const remove = async (q) => {
    try {
      await axios.delete(`${API}/enquiries/${q.id}`, auth());
      setEnquiries((list) => list.filter((e) => e.id !== q.id));
      setConfirmDelete(null);
    } catch {
      toast.error("Could not delete that enquiry.");
    }
  };

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/enquiries`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setEnquiries(r.data))
      .catch(() => {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
      });
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { passcode });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch {
      toast.error("Invalid passcode.");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setEnquiries(null);
  };

  return (
    <main data-testid="admin-page" className="flex min-h-screen flex-col bg-obsidian">
      <div className="mx-auto w-full max-w-4xl flex-1 px-4 py-12 sm:px-8">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="admin-back-link">
            <Logo />
          </Link>
          {token && (
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 transition-colors hover:border-ruby/50 hover:text-ruby"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          )}
        </div>

        {!token ? (
          <motion.form
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            onSubmit={login}
            className="mx-auto mt-24 max-w-sm rounded-2xl border border-white/10 bg-card2/70 p-10"
            data-testid="admin-login-form"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-ruby/30 bg-ruby/10">
              <Lock className="h-5 w-5 text-ruby" />
            </span>
            <h1 className="mt-6 font-heading text-2xl font-semibold text-slate-50">Owner access</h1>
            <p className="mt-2 text-sm text-slate-400">Enter your passcode to view enquiries.</p>
            <input
              type="password"
              data-testid="admin-passcode-input"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              required
              className="mt-6 w-full rounded-xl border border-white/10 bg-obsidian/60 px-5 py-3.5 text-base text-slate-100 outline-none transition-colors focus:border-ruby/60"
            />
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={busy}
              className="btn-magnetic mt-5 w-full rounded-full bg-gradient-to-r from-[#E5484D] to-[#C8102E] px-8 py-3.5 font-heading text-base font-semibold text-white disabled:opacity-60"
            >
              {busy ? "Checking…" : "Unlock"}
            </button>
          </motion.form>
        ) : (
          <div className="mt-14">
            <p className="overline-tag mb-3">Inbox</p>
            <h1 className="font-heading text-3xl font-semibold text-slate-50" data-testid="admin-inbox-title">
              Enquiries {enquiries && <span className="text-ruby">({enquiries.length})</span>}
            </h1>
            {!enquiries ? (
              <p className="mt-10 text-slate-500" data-testid="admin-loading">Loading…</p>
            ) : enquiries.length === 0 ? (
              <div className="mt-10 flex items-center gap-4 rounded-2xl border border-white/10 bg-card2/60 p-8 text-slate-400" data-testid="admin-empty">
                <Inbox className="h-5 w-5 text-ruby" /> No enquiries yet.
              </div>
            ) : (
              <ul className="mt-8 space-y-5" data-testid="enquiry-list">
                {enquiries.map((q) => (
                  <li
                    key={q.id}
                    className={`rounded-2xl border bg-card2/60 p-7 transition-opacity duration-300 ${
                      q.replied ? "border-white/5 opacity-55" : "border-white/10"
                    }`}
                    data-testid={`enquiry-item-${q.id}`}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="flex items-center gap-3 font-heading text-lg font-semibold text-slate-50">
                        {q.name}
                        {q.replied && (
                          <span
                            data-testid={`enquiry-replied-badge-${q.id}`}
                            className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400"
                          >
                            Replied
                          </span>
                        )}
                      </span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        {new Date(q.created_at).toLocaleString("en-AU")}
                      </span>
                    </div>
                    <a href={`mailto:${q.email}`} className="mt-1 block text-sm text-ruby hover:underline">
                      {q.email}
                    </a>
                    <p className="mt-4 whitespace-pre-wrap text-base leading-relaxed text-slate-300">{q.message}</p>
                    <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-white/5 pt-5">
                      <button
                        data-testid={`enquiry-replied-toggle-${q.id}`}
                        onClick={() => toggleReplied(q)}
                        className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 transition-colors hover:border-emerald-500/50 hover:text-emerald-400"
                      >
                        {q.replied ? (
                          <>
                            <RotateCcw className="h-3.5 w-3.5" /> Mark Unread
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="h-3.5 w-3.5" /> Mark Replied
                          </>
                        )}
                      </button>
                      {confirmDelete === q.id ? (
                        <>
                          <button
                            data-testid={`enquiry-delete-confirm-${q.id}`}
                            onClick={() => remove(q)}
                            className="flex items-center gap-2 rounded-full border border-ruby/60 bg-ruby/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ruby transition-colors hover:bg-ruby/25"
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Confirm Delete
                          </button>
                          <button
                            data-testid={`enquiry-delete-cancel-${q.id}`}
                            onClick={() => setConfirmDelete(null)}
                            className="rounded-full border border-white/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-slate-300"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          data-testid={`enquiry-delete-${q.id}`}
                          onClick={() => setConfirmDelete(q.id)}
                          className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 transition-colors hover:border-ruby/50 hover:text-ruby"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
